// Lightweight client-side performance instrumentation.
//
// Logs labelled durations to the console so we can see where page-load time goes - edge functions,
// calendar fetches, DB queries, etc. Each timed span also emits a `performance.measure`, so it shows
// up in the browser DevTools → Performance timeline too.
//
// Enabled automatically in dev. In any build, toggle at runtime from the console:
//   localStorage.setItem('upstand_perf', '1')   // force on
//   localStorage.setItem('upstand_perf', '0')   // force off
// then reload.

function resolveEnabled(): boolean {
  try {
    const flag = typeof localStorage !== 'undefined' ? localStorage.getItem('upstand_perf') : null;
    if (flag === '1') return true;
    if (flag === '0') return false;
  } catch {
    /* localStorage unavailable (SSR / privacy mode) - fall through to the dev default */
  }
  return Boolean(import.meta.env?.DEV);
}

const PERF_ENABLED = resolveEnabled();

export function perfEnabled(): boolean {
  return PERF_ENABLED;
}

function logDuration(label: string, start: number, ms: number, extra?: string): void {
  const rounded = Math.round(ms);
  // Red ≥1.5s (the "AGES" ones), amber ≥500ms, green otherwise.
  const color = rounded >= 1500 ? '#c0392b' : rounded >= 500 ? '#b8860b' : '#1d9e75';
  // eslint-disable-next-line no-console
  console.log(`%c[perf] ${label} - ${rounded}ms${extra ? ` · ${extra}` : ''}`, `color:${color};font-weight:500`);
  try {
    performance.measure(`perf:${label}`, { start, duration: ms });
  } catch {
    /* performance.measure with a duration option isn't supported everywhere - console log is enough */
  }
}

/**
 * Time an async operation and log its duration. No-op (just runs `fn`) when perf logging is off.
 * Accepts any thenable so Supabase query builders (`PromiseLike`, not real Promises) can be wrapped
 * directly, e.g. `timed('db:x', () => supabase.from(...).select(...))`.
 */
export async function timed<T>(label: string, fn: () => PromiseLike<T>): Promise<T> {
  if (!PERF_ENABLED) return fn();
  const start = performance.now();
  let ok = true;
  try {
    return await fn();
  } catch (e) {
    ok = false;
    throw e;
  } finally {
    logDuration(label, start, performance.now() - start, ok ? undefined : 'failed');
  }
}

/**
 * Manual timer for a span that isn't a single promise (e.g. an effect that does several awaits).
 * Returns a `stop(extra?)` function - call it when the span finishes. No-op when perf logging is off.
 */
export function startTimer(label: string): (extra?: string) => void {
  if (!PERF_ENABLED) return () => {};
  const start = performance.now();
  return (extra?: string) => logDuration(label, start, performance.now() - start, extra);
}
